import React from "react";
import {
  ALL_SCOPES,
  SCOPE_HIERARCHY,
  getParentScope,
} from "./scopeHierarchy";
import { resolveScopeControlState } from "./departmentScopes";

/**
 * ScopeTree — renders all 12 scopes in a hierarchical checkbox tree.
 *
 * Parent scopes (supervisor, semantic) appear at the top level with their
 * children indented beneath. When a parent is checked, children are shown
 * as checked + disabled with an "inherited" label. When a parent is unchecked,
 * children become independently toggleable.
 *
 * Department-inherited scopes are also rendered checked + disabled + "inherited".
 * When the lock is department-derived, a source element naming the granting
 * departments is rendered and the checkbox's aria-describedby points at it.
 *
 * @param {Object} props
 * @param {string[]} props.selectedScopes - Currently selected/active scopes (direct grants only).
 * @param {(scopes: string[]) => void} props.onChange - Called with the new scope set when toggled (direct grants only).
 * @param {boolean} [props.disabled] - Disables all controls when true.
 * @param {string[]} [props.inheritedScopes] - Department-derived scopes, default [].
 * @param {Object.<string, string[]>} [props.inheritedSources] - Department names per scope, default {}.
 */
export default function ScopeTree({
  selectedScopes = [],
  onChange,
  disabled = false,
  inheritedScopes = [],
  inheritedSources = {},
}) {
  const handleToggle = (scope) => {
    if (disabled) return;

    // Early return when the row is locked — onChange can never emit an inherited scope
    const state = resolveScopeControlState({
      scope,
      directScopes: selectedScopes,
      inheritedDepartmentScopes: inheritedScopes,
    });
    if (state.disabled) return;

    const isCurrentlySelected = selectedScopes.includes(scope);

    if (isCurrentlySelected) {
      // Remove this scope
      const updated = selectedScopes.filter((s) => s !== scope);
      onChange(updated);
    } else {
      // Add this scope
      const updated = [...selectedScopes, scope];
      onChange(updated);
    }
  };

  // Build the tree structure: top-level scopes with optional children
  const treeItems = [];

  for (const scope of ALL_SCOPES) {
    const parent = getParentScope(scope);
    // Skip children — they are rendered nested under their parent
    if (parent) continue;

    const children = SCOPE_HIERARCHY[scope] || null;
    treeItems.push({ scope, children });
  }

  /**
   * Renders a single scope row (top-level or child).
   */
  const renderScopeRow = (scope, isChild) => {
    const state = resolveScopeControlState({
      scope,
      directScopes: selectedScopes,
      inheritedDepartmentScopes: inheritedScopes,
    });

    const isDisabled = disabled || state.disabled;
    const sourceId = `${scope}-inherited-source`;
    const sources = state.departmentInherited ? (inheritedSources[scope] || []) : [];
    const hasSource = sources.length > 0;

    return (
      <label
        key={scope}
        className={`scope-tree__item${isChild ? " scope-tree__item--child" : ""}`}
      >
        <input
          type="checkbox"
          className="scope-tree__checkbox"
          checked={state.checked}
          onChange={() => handleToggle(scope)}
          disabled={isDisabled}
          aria-label={`Toggle ${scope} scope`}
          {...(hasSource ? { "aria-describedby": sourceId } : {})}
        />
        <span className="scope-tree__label">{scope}</span>
        {state.inherited && (
          <span className="scope-tree__inherited-badge">inherited</span>
        )}
        {hasSource && (
          <span id={sourceId} className="scope-tree__inherited-source">
            {sources.join(", ")}
          </span>
        )}
      </label>
    );
  };

  return (
    <div className="scope-tree" role="group" aria-label="Scope selection">
      {treeItems.map(({ scope, children }) => (
        <div key={scope} className="scope-tree__group">
          {/* Parent / standalone scope */}
          {renderScopeRow(scope, false)}

          {/* Children (only rendered if this scope has children) */}
          {children && (
            <div className="scope-tree__children">
              {children.map((child) => renderScopeRow(child, true))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
